import { apiClient } from "@base/api/client";
import type {
  CreateMembershipUpgradeRequest,
  CreateMembershipUpgradeResponse,
  MembershipPlanType,
  UpgradeCalculation,
} from "@features/membership/model/types";

/**
 * GET /82office/membership/api/upgrade?upgrade_type={type}
 * 멤버십 업그레이드 금액 조회 (선택한 플랜 타입별 차액/청구 금액)
 *
 * ※ 동일 엔드포인트가 params 유무에 따라 다른 응답을 반환함.
 *   - params 없음 → fetchEligiblePlans (EligiblePlansResponse)
 *   - upgrade_type 있음 → 이 함수 (UpgradeCalculation)
 *
 * Error: MEMBERSHIP_NOT_FOUND - No such membership was found: {type}. (Subscription이 없거나 판매 종료 플랜 요청)
 */
export async function fetchUpgradeCalculation(upgradeType: MembershipPlanType): Promise<UpgradeCalculation> {
  const { data } = await apiClient.get<UpgradeCalculation>("/82office/membership/api/upgrade", {
    params: { upgrade_type: upgradeType },
  });
  return data;
}

/**
 * POST /82office/membership/api/upgrade
 * 멤버십 업그레이드 신청 - hosted_page(외부 결제 링크) 반환
 *
 * Error: MEMBERSHIP_NOT_FOUND - No such membership was found: {type}. (판매 종료 플랜 요청)
 */
export async function createMembershipUpgrade(type: MembershipPlanType): Promise<CreateMembershipUpgradeResponse> {
  const body: CreateMembershipUpgradeRequest = { type };
  const { data } = await apiClient.post<CreateMembershipUpgradeResponse>("/82office/membership/api/upgrade", body);
  return data;
}

/*
 * TanStack Query 사용 예시:
 *
 * // 업그레이드 금액 조회 (useQuery) - Eligible plan 선택 시
 * import { useQuery } from "@tanstack/react-query";
 * import { fetchUpgradeCalculation } from "@features/membership/api/upgrade";
 *
 * const [selectedPlanType, setSelectedPlanType] = useState<MembershipPlanType | null>(null);
 * const { data: calculation } = useQuery({
 *   queryKey: ["upgradeCalculation", selectedPlanType],
 *   queryFn: () => fetchUpgradeCalculation(selectedPlanType!),
 *   enabled: !!selectedPlanType,
 * });
 *
 * // 업그레이드 신청 (useMutation)
 * import { useMutation } from "@tanstack/react-query";
 * import { createMembershipUpgrade } from "@features/membership/api/upgrade";
 *
 * const upgradeMutation = useMutation({
 *   mutationFn: createMembershipUpgrade,
 *   onSuccess: (data) => {
 *     window.location.href = data.hosted_page;
 *   },
 * });
 * upgradeMutation.mutate("SILVER");
 */
